import { useEffect } from 'react';
import { create } from 'zustand';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

/**
 * Sağ üstte beliren geçici bildirimler (docs/05 §6).
 * Basit bir zustand deposu — ek bağımlılık gerektirmiyor.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (tone, message) =>
    set((state) => ({ toasts: [...state.toasts, { id: nextId++, tone, message }] })),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) })),
}));

/** Bileşen dışından da çağrılabilsin diye doğrudan store üzerinden. */
export const toast = {
  success: (message: string) => useToastStore.getState().push('success', message),
  error: (message: string) => useToastStore.getState().push('error', message),
  info: (message: string) => useToastStore.getState().push('info', message),
};

const tones: Record<ToastTone, string> = {
  success: 'border-primary-200 bg-primary-50 text-primary-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-sky-200 bg-sky-50 text-sky-900',
};

const AUTO_DISMISS_MS = 5000;

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-4 top-4 z-50 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} />
      ))}
    </div>
  );
}

function ToastItem({ toast: item }: { toast: Toast }) {
  const dismiss = useToastStore((state) => state.dismiss);

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(item.id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [dismiss, item.id]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-card border p-3.5 text-sm shadow-lg',
        tones[item.tone],
      )}
    >
      <span className="flex-1 leading-relaxed">{item.message}</span>
      <button
        type="button"
        onClick={() => dismiss(item.id)}
        aria-label={t('common.close')}
        className="rounded-md px-1 text-lg leading-none opacity-60 hover:opacity-100"
      >
        ×
      </button>
    </div>
  );
}
